/**
 * useFingeringEditorDesktop (issue #22 Phase C)
 *
 * PC (mouse + keyboard) 固有のジェスチャ・state・lifecycle を担当する hook。
 * spec: docs/superpowers/specs/2026-05-24-fingering-editor-3layer-split-design.md §3.2.3
 * plan: docs/superpowers/plans/2026-05-24-fingering-editor-3layer-split.md Phase C
 *
 * 元 useFingeringEditor.ts の mouse / wheel / Cmd+Z / panDrag 経路を抽出。
 * 元のロジックを忠実に再現することを最優先 (元コード L230, 257, 468-526, 561-574, 647-805, 953-963)。
 *
 * NOTE: 本ファイルは Phase C 時点では未使用 (誰からも呼ばれない)。Phase D で wrapper の compose 元になる。
 */
import { useState, useCallback, useRef, useEffect, MutableRefObject } from 'react';
import { UseFingeringEditorCoreReturn } from './useFingeringEditorCore';
import { PanDragState } from './useFingeringEditor';
import { DragState } from '../types/fingeringEditor';
import { dragDebug } from '../pages/fingering/components/editor/dragDebug';

const DRAG_THRESHOLD_PX = 5;
const DRAG_THRESHOLD_SQ = DRAG_THRESHOLD_PX * DRAG_THRESHOLD_PX;

// issue #16 Phase 5: 範囲選択 drag の internal state。
// PC mouse + selectMode 限定で起動 (touch / pen は無視)、 spec §6.2 の競合解決ルールを満たす。
export interface SelectDragState {
  startSvgX: number;
  startSvgY: number;
  startClientX: number;
  startClientY: number;
  pointerId: number;
  page: number;
  // 'pending' → 閾値超過で 'dragging' に昇格
  phase: 'pending' | 'dragging';
  // Ctrl/Cmd+drag の追加選択モード
  additive: boolean;
}

export interface UseFingeringEditorDesktopReturn {
  state: {
    isPanDragging: boolean;
    setIsPanDragging: React.Dispatch<React.SetStateAction<boolean>>;
  };
  refs: {
    panDragRef: MutableRefObject<PanDragState | null>;
    // issue #16 Phase 5: PC mouse 専用 範囲選択 drag state
    selectDragRef: MutableRefObject<SelectDragState | null>;
  };
  handlers: {
    handleSvgPointerDown: (e: React.PointerEvent<SVGSVGElement>) => void;
    handleBadgeDragStart: (id: number, e: React.PointerEvent) => void;
    handlePointerMove: (e: React.PointerEvent<SVGSVGElement>) => void;
    handlePointerUp: () => void;
    handlePointerLeave: (e?: React.PointerEvent<SVGSVGElement>) => void;
  };
}

export function useFingeringEditorDesktop(core: UseFingeringEditorCoreReturn): UseFingeringEditorDesktopReturn {
  // --- Desktop 専用 state ---
  const [isPanDragging, setIsPanDragging] = useState(false);

  // --- Desktop 専用 ref ---
  const panDragRef = useRef<PanDragState | null>(null);
  // issue #16 Phase 5: PC mouse 専用 範囲選択 drag (selectMode + (zoom<=1 or Shift) で起動)
  const selectDragRef = useRef<SelectDragState | null>(null);

  // --- e5 onWheel listener (元 L468-486) ---
  // ガード: mobile では desktop wheel listener は no-op
  useEffect(() => {
    if (core.view.isMobile) return;
    const el = core.refs.scrollContainerRef.current;
    if (!el) return;

    const onWheel = (e: WheelEvent) => {
      if (!el.contains(e.target as Node)) return;
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        const currentZoom = core.refs.zoomRef.current;
        const nextZoom = Math.min(5, Math.max(1, currentZoom - e.deltaY * 0.01));
        core.handlers.applyZoomWithPivot(e.clientX, e.clientY, nextZoom);
      } else if (core.refs.zoomRef.current > 1) {
        e.preventDefault();
        const dx = e.shiftKey ? e.deltaY : e.deltaX;
        const dy = e.shiftKey ? 0 : e.deltaY;
        const next = { x: core.refs.panRef.current.x - dx, y: core.refs.panRef.current.y - dy };
        core.view.setPan(core.handlers.applyPanClamp(next));
      }
    };

    // 元コードは document に登録していたので互換のため同様にする
    document.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      document.removeEventListener('wheel', onWheel);
    };
    // 元 e5 と同じく fingeringData 依存
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [core.view.isMobile, core.data.fingeringData]);

  // --- e6 Cmd+Z + 範囲選択 keydown listener (元 L953-962 + issue #16 Phase 5 / spec §7) ---
  useEffect(() => {
    if (core.view.isMobile) return;
    const onKeyDown = (e: KeyboardEvent) => {
      // panel 編集中の input/textarea にフォーカスがあるとき、 Delete / Backspace / a / Esc を吸わない (文字入力保護)。
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;
      const isEditable =
        tag === 'INPUT' || tag === 'TEXTAREA' || (target?.isContentEditable ?? false);
      // Cmd+Z (既存) は editable でも誤動作しないよう Backspace 等とは別経路で先に処理。
      if ((e.metaKey || e.ctrlKey) && e.key === 'z' && !e.shiftKey) {
        if (isEditable) return; // editable では browser undo に委ねる
        e.preventDefault();
        core.handlers.handleUndo();
        return;
      }
      if (isEditable) return;
      // Cmd/Ctrl+A: 現ページ全選択
      if ((e.metaKey || e.ctrlKey) && (e.key === 'a' || e.key === 'A')) {
        e.preventDefault();
        const ids = core.data.pageBadges.map((b) => b.id);
        core.handlers.selectRange(ids);
        return;
      }
      // Delete / Backspace: 選択中なら一括削除
      if ((e.key === 'Delete' || e.key === 'Backspace') && core.mode.selectedIds.size > 0) {
        e.preventDefault();
        core.handlers.handleDeleteMany(Array.from(core.mode.selectedIds));
        return;
      }
      // Escape: 選択解除
      if (e.key === 'Escape' && core.mode.selectedIds.size > 0) {
        e.preventDefault();
        core.handlers.clearSelection();
        return;
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [
    core.view.isMobile, core.handlers, core.mode.selectedIds, core.data.pageBadges,
  ]);

  // --- handleSvgPointerDown (元 L561-574 + issue #16 Phase 5 範囲選択 / v3 Phase D Bug#1) ---
  // issue #16 v3 Phase D (Bug #1): zoom 制約を撤廃。 spec §3.2 確定ルール:
  //   selectMode ON (Desktop は常時 true、 モードレス) → zoom 不問で常に範囲選択 drag
  //   PC の pan は wheel (onWheel L80-86、 zoom>1時のスクロール) に委譲する。
  // 旧 spec §6.2 の「zoom>1 + Shift なし = pan drag」「zoom<=1 で無視」は v3 で廃止。
  // ↓ panDragRef 初期化経路 (L下部) は到達不能 (dead) になるが、 panDragRef は wrapper / page test が
  //   型参照するため宣言・ガードは残す (物理削除は別 cleanup タスク)。
  const handleSvgPointerDown = useCallback((e: React.PointerEvent<SVGSVGElement>) => {
    if (e.pointerType !== 'mouse') return;
    if (e.button !== 0) return;
    if (core.refs.addModeRef.current) return;
    if (core.refs.editorModeRef.current !== 'edit') return;
    const selectMode = core.mode.selectMode;
    const zoom = core.refs.zoomRef.current;
    // Bug #1: zoom 制約 (旧: e.shiftKey || zoom <= 1) を撤廃。 selectMode のみで判定。
    const shouldRangeSelect = selectMode;
    if (shouldRangeSelect) {
      // 範囲選択 drag を初期化。 SVG 座標は move 時に確定するため client 座標だけ覚える。
      const svgPt = core.handlers.clientToSvg(e.clientX, e.clientY);
      selectDragRef.current = {
        startSvgX: svgPt?.x ?? 0,
        startSvgY: svgPt?.y ?? 0,
        startClientX: e.clientX,
        startClientY: e.clientY,
        pointerId: e.pointerId,
        page: core.view.currentPage,
        phase: 'pending',
        additive: e.ctrlKey || e.metaKey,
      };
      return;
    }
    // dead path (selectMode は Desktop 常時 true): 型互換のため残置。
    if (zoom <= 1) return;
    panDragRef.current = {
      startClientX: e.clientX,
      startClientY: e.clientY,
      startPan: { ...core.refs.panRef.current },
      phase: 'pending',
      pointerId: e.pointerId,
    };
  }, [core.refs, core.mode.selectMode, core.handlers, core.view.currentPage]);

  // issue #16 Bug #5: PC mouse 用の個別 badge drag entry。
  // wrapper (useFingeringEditor.ts) で pointerType==='mouse' のみ振り分けられる。
  // Core の tryStartDrag を呼ぶだけ。 long-press 等のプラットフォーム固有処理は Mobile hook 専用。
  const handleBadgeDragStart = useCallback((id: number, e: React.PointerEvent) => {
    if (e.pointerType !== 'mouse') return;
    core.handlers.tryStartDrag(id, e);
  }, [core.handlers]);

  // --- handlePointerMove (元 L647-709) mouse 経路 ---
  const handlePointerMove = useCallback((e: React.PointerEvent<SVGSVGElement>) => {
    if (e.pointerType !== 'mouse') return;
    // issue #16 Phase 5: 範囲選択 drag (panDrag より前に処理)
    const selectDrag = selectDragRef.current;
    if (selectDrag) {
      const dx = e.clientX - selectDrag.startClientX;
      const dy = e.clientY - selectDrag.startClientY;
      if (selectDrag.phase === 'pending') {
        const distSq = dx * dx + dy * dy;
        if (distSq < DRAG_THRESHOLD_SQ) return;
        selectDrag.phase = 'dragging';
        try { core.refs.svgRef.current?.setPointerCapture?.(e.pointerId); } catch { /* jsdom 等を許容 */ }
      }
      // svg 座標が取れないとき (jsdom 等で rect=0) は client delta を流用して矩形 visible のみ更新する。
      const svgPt = core.handlers.clientToSvg(e.clientX, e.clientY);
      const currentSvgX = svgPt?.x ?? selectDrag.startSvgX + dx;
      const currentSvgY = svgPt?.y ?? selectDrag.startSvgY + dy;
      core.mode.setSelectRectVisible({
        startSvgX: selectDrag.startSvgX,
        startSvgY: selectDrag.startSvgY,
        currentSvgX,
        currentSvgY,
        page: selectDrag.page,
      });
      return;
    }
    // PC マウスドラッグでのパン処理 (badge drag より優先)
    const panDrag = panDragRef.current;
    if (panDrag) {
      const dx = e.clientX - panDrag.startClientX;
      const dy = e.clientY - panDrag.startClientY;
      if (panDrag.phase === 'pending') {
        const distSq = dx * dx + dy * dy;
        if (distSq < DRAG_THRESHOLD_SQ) return;
        panDrag.phase = 'dragging';
        setIsPanDragging(true);
        try { core.refs.svgRef.current?.setPointerCapture?.(e.pointerId); } catch { /* jsdom 等を許容 */ }
      }
      const next = { x: panDrag.startPan.x + dx, y: panDrag.startPan.y + dy };
      core.view.setPan(core.handlers.applyPanClamp(next));
      return;
    }
    // mouse の通常 badge drag (panDrag 中以外)
    const d = core.refs.dragRef.current;
    if (!d) return;
    if (d.phase === 'pending') {
      const dxClient = e.clientX - d.startClientX;
      const dyClient = e.clientY - d.startClientY;
      const distSq = dxClient * dxClient + dyClient * dyClient;
      if (core.mode.addMode) {
        if (distSq > DRAG_THRESHOLD_SQ) {
          dragDebug('phase=null (addMode tap-cancel)');
          core.refs.dragRef.current = null;
          core.refs.clickSuppressRef.current = true;
        }
        return;
      }
      if (distSq < DRAG_THRESHOLD_SQ) return;
      dragDebug(`phase=dragging mode=${core.refs.editorModeRef.current} type=${e.pointerType}`);
      d.phase = 'dragging';
      core.drag.setDragBadgeId(d.badgeId);
      try { core.refs.svgRef.current?.setPointerCapture?.(e.pointerId); } catch { /* jsdom 等を許容 */ }
      return;
    }
    const svgPt = core.handlers.clientToSvg(e.clientX, e.clientY);
    if (!svgPt) return;
    d.currentX = svgPt.x;
    d.currentY = svgPt.y;
    core.drag.setDragDelta({ dx: svgPt.x - d.startSvgX, dy: svgPt.y - d.startSvgY });
  }, [core.handlers, core.mode, core.refs, core.drag, core.view]);

  // --- handlePointerUp (元 L758-785 + issue #16 Phase 5 範囲選択 finalize) mouse 経路 ---
  const handlePointerUp = useCallback(() => {
    // TODO: REMOVE AFTER #27 DEBUG
    dragDebug(`up phase=${core.refs.dragRef.current?.phase ?? 'null'}`);
    // issue #16 Phase 5 / v3 Phase E+F (DRY): 範囲選択 drag の finalize (panDrag より前)。
    //   finalize 手続き (min/max→badgesInRect→selectRange→clickSuppress→クリア) は
    //   Core の commitRangeSelectFromVisible に集約 (Mobile handleTouchEnd と共通化、 二重実装解消)。
    const selectDrag = selectDragRef.current;
    if (selectDrag) {
      if (selectDrag.phase === 'dragging') {
        core.handlers.commitRangeSelectFromVisible({ additive: selectDrag.additive });
      } else {
        // pending (= tap): 既に複数選択中ならクリア、 そうでなければ既存挙動に委ねる (page 側 dismissPanel)。
        if (core.mode.isMultiSelect) {
          core.handlers.clearSelection();
        }
        core.mode.setSelectRectVisible(null);
      }
      selectDragRef.current = null;
      return;
    }
    const panDrag = panDragRef.current;
    if (panDrag) {
      const wasDragging = panDrag.phase === 'dragging';
      panDragRef.current = null;
      setIsPanDragging(false);
      if (wasDragging) core.refs.clickSuppressRef.current = true;
      return;
    }
    const d = core.refs.dragRef.current;
    if (!d) return;
    if (d.phase === 'pending') {
      core.refs.dragRef.current = null;
      core.refs.clickSuppressRef.current = true;
      if (core.mode.addMode) {
        if (d.beatIndex !== null) core.handlers.addNoteToBeat(d.beatIndex);
        return;
      }
      // Desktop では editorMode は常に 'edit' 固定だが、 元コードに合わせて条件分岐は残す
      if (core.mode.editorMode !== 'move') {
        core.handlers.handleBadgeSelect(d.badgeId, d.startClientX, d.startClientY);
      }
      return;
    }
    // issue #149: 複数選択群 move 後の選択維持 (finalize 直後の合成 click 抑止) は
    // Core.finalizeDrag に集約済み (mobile/desktop 両経路が通る唯一の合流点)。 desktop 側で
    // 重ねて clickSuppress を立てる必要はない。
    core.handlers.finalizeDrag(d as DragState);
  }, [core.handlers, core.mode, core.refs]);

  // --- handlePointerLeave (元 L787-804) mouse 経路 ---
  const handlePointerLeave = useCallback((e?: React.PointerEvent<SVGSVGElement>) => {
    // issue #16 Phase 5: 範囲選択 drag 中の leave は visible rect とともにクリア (drag 自動 cancel)
    const selectDrag = selectDragRef.current;
    if (selectDrag) {
      selectDragRef.current = null;
      core.mode.setSelectRectVisible(null);
      return;
    }
    const panDrag = panDragRef.current;
    if (panDrag) {
      panDragRef.current = null;
      setIsPanDragging(false);
      return;
    }
    const d = core.refs.dragRef.current;
    if (!d) return;
    // V3-3: capture を保持中の pointerleave は無視
    if (e && core.refs.svgRef.current?.hasPointerCapture?.(e.pointerId)) return;
    if (d.phase === 'dragging') {
      core.handlers.finalizeDrag(d as DragState);
    } else {
      core.refs.dragRef.current = null;
    }
  }, [core.handlers, core.refs, core.mode]);

  return {
    state: { isPanDragging, setIsPanDragging },
    refs: { panDragRef, selectDragRef },
    handlers: {
      handleSvgPointerDown,
      handleBadgeDragStart,
      handlePointerMove,
      handlePointerUp,
      handlePointerLeave,
    },
  };
}
